// Toast notifications — the app's single feedback channel for mutations
// (acknowledge, assign, save, delete, ticket-created…). Top-right stack, glass
// styled, auto-dismissing, dismissible, and announced to screen readers via an
// aria-live region. Replaces the per-page bespoke success/error divs.
//
// Usage:
//   const toast = useToast();
//   toast.success('Saved');
//   toast.error('Could not save');
//   toast.show({ tone: 'success', message: 'Ticket created', action: { label: 'View', onClick: () => nav(`/tickets/${id}`) } });

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, AlertTriangle, Info, X } from 'lucide-react';

type Tone = 'success' | 'error' | 'info';

type ToastAction = { label: string; onClick: () => void };

type ToastInput = {
  message: ReactNode;
  tone?: Tone;
  /** ms before auto-dismiss; 0 = sticky (must be dismissed). Default 4000 (errors 7000). */
  duration?: number;
  action?: ToastAction;
};

type ToastItem = Required<Omit<ToastInput, 'action'>> & { id: number; action?: ToastAction };

type ToastApi = {
  show: (t: ToastInput) => void;
  success: (message: ReactNode, opts?: Omit<ToastInput, 'message' | 'tone'>) => void;
  error: (message: ReactNode, opts?: Omit<ToastInput, 'message' | 'tone'>) => void;
  info: (message: ReactNode, opts?: Omit<ToastInput, 'message' | 'tone'>) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

/** Hook to push toasts. Throws if used outside <ToastProvider>. */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}

const TONE_ICON: Record<Tone, typeof CheckCircle2> = {
  success: CheckCircle2,
  error: AlertTriangle,
  info: Info,
};
const TONE_CLASS: Record<Tone, string> = {
  success: 'text-ok',
  error: 'text-critical',
  info: 'text-primary',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);

  const dismiss = useCallback((id: number) => {
    setItems((cur) => cur.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((input: ToastInput) => {
    const tone = input.tone ?? 'info';
    const duration = input.duration ?? (tone === 'error' ? 7000 : 4000);
    const id = ++seq.current;
    setItems((cur) => [...cur, { id, message: input.message, tone, duration, action: input.action }]);
  }, []);

  const api: ToastApi = {
    show,
    success: (message, opts) => show({ ...opts, message, tone: 'success' }),
    error: (message, opts) => show({ ...opts, message, tone: 'error' }),
    info: (message, opts) => show({ ...opts, message, tone: 'info' }),
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <div
            className="pointer-events-none fixed top-4 z-toast flex w-full max-w-sm flex-col gap-2 px-4 end-0 sm:px-0 sm:end-4"
            role="region"
            aria-live="polite"
            aria-label="Notifications"
          >
            {items.map((t) => (
              <ToastCard key={t.id} item={t} onDismiss={() => dismiss(t.id)} />
            ))}
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  );
}

function ToastCard({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const Icon = TONE_ICON[item.tone];
  useEffect(() => {
    if (!item.duration) return;
    const h = window.setTimeout(onDismiss, item.duration);
    return () => window.clearTimeout(h);
  }, [item.duration, onDismiss]);

  return (
    <div
      className="tk-modal tk-toast-in pointer-events-auto flex items-start gap-3 p-3 text-sm shadow-2xl"
      role={item.tone === 'error' ? 'alert' : 'status'}
    >
      <Icon size={18} className={`mt-0.5 shrink-0 ${TONE_CLASS[item.tone]}`} />
      <div className="min-w-0 flex-1">
        <div className="text-fg">{item.message}</div>
        {item.action && (
          <button
            className="mt-1 text-xs font-semibold text-primary hover:underline"
            onClick={() => { item.action!.onClick(); onDismiss(); }}
          >
            {item.action.label}
          </button>
        )}
      </div>
      <button
        className="tk-btn-ghost -mt-1 -me-1 shrink-0 p-1.5"
        onClick={onDismiss}
        aria-label="Dismiss notification"
      >
        <X size={14} />
      </button>
    </div>
  );
}
